/**
 * Cold-load helpers for document deep links (every-frame: no Home flash).
 * Kept separate from document-deep-link-pending.ts to avoid a cycle with app-routes.
 */
import type { OpenTab } from '@/components/features/document-tabs'
import { findOpenTabForDocumentPath, isUuid, parseAppPath } from '@/lib/app-routes'
import {
  getPendingDocumentDeepLink,
  markColdLoadCaptureAttempted,
  setPendingDocumentDeepLink,
  wasColdLoadCaptureAttempted,
  type PendingDocumentDeepLink,
} from '@/lib/document-deep-link-pending'

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** Title hint for provisional tab / loading chrome (never opaque ws/ hex roots). */
/**
 * True when a cold-load provisional tab has become a real document or explorer.
 * `/doc/{uuid}` provisionals use the UUID as both id and filePath - that is not loaded yet.
 * A resolved note has a storage filePath with a slash (workspace root + rest).
 */
export function isColdLoadDocumentTabResolved(tab: OpenTab | undefined): boolean {
  if (!tab) return false
  if (tab.type === 'explorer') return true
  if (tab.type === 'home') return false
  const fp = (tab.filePath ?? '').replace(/^\/+/, '')
  return isUuid(tab.id) && fp.includes('/')
}

/**
 * Pending restore may stop only when this tab is the fetched document.
 * A `/doc/{uuid}` provisional uses the UUID as both id and filePath. That is not open yet.
 */
export function shareLinkTabCountsAsOpen(
  tab: OpenTab | undefined,
  activeTabId: string | null,
): boolean {
  if (!tab || !activeTabId || activeTabId === 'home') return false
  if (tab.id !== activeTabId || tab.type === 'home') return false
  return isColdLoadDocumentTabResolved(tab)
}

export function titleHintFromPathOrId(pathOrId: string): string {
  const trimmed = pathOrId.replace(/^\/+/, '').replace(/\/+$/, '')
  const base = trimmed.split('/').pop() || 'Document'
  return base.replace(/\.(md|html)$/i, '') || 'Document'
}

/**
 * True when this page load is a browser reload (Cmd+R / hard refresh).
 * Reload must keep localStorage open-tabs; only *navigate* cold loads
 * (new Chrome tab, pasted share URL) isolate to a solo document session.
 */
export function isBrowserReloadNavigation(
  perf: Pick<Performance, 'getEntriesByType'> & {
    navigation?: { type?: number }
  } = typeof performance !== 'undefined' ? performance : { getEntriesByType: () => [] },
): boolean {
  const entries = perf.getEntriesByType?.('navigation') as
    | PerformanceNavigationTiming[]
    | undefined
  if (entries?.[0]?.type === 'reload') return true
  // Legacy PerformanceNavigation.TYPE_RELOAD === 1
  return perf.navigation?.type === 1
}

/**
 * Resolve a document deep link from a pathname without waiting for workspace list.
 * Bare single-segment slugs are excluded (they become workspace-home once orgs load).
 */
export function resolveDocumentDeepLinkFromPathname(
  pathname: string,
): PendingDocumentDeepLink | null {
  if (!pathname || pathname === '/') return null

  const intent = parseAppPath(pathname, new URLSearchParams(), [])
  if (intent.type === 'legacy-redirect') {
    return resolveDocumentDeepLinkFromPathname(intent.targetPath)
  }
  if (intent.type !== 'document') return null

  const pathOrId = intent.pathOrId.replace(/^\/+/, '')
  if (!pathOrId) return null
  if (!pathOrId.includes('/') && !UUID_RE.test(pathOrId)) {
    return null
  }

  return { pathname, pathOrId }
}

/**
 * Capture address-bar document intent once on cold load (before first paint consumers).
 * Idempotent; safe to call from layout hooks and useUrlSync.
 */
export function captureColdLoadDocumentDeepLink(
  pathname: string = typeof window !== 'undefined' ? window.location.pathname : '',
): PendingDocumentDeepLink | null {
  const existing = getPendingDocumentDeepLink()
  if (existing) return existing
  if (wasColdLoadCaptureAttempted()) return null

  markColdLoadCaptureAttempted()
  const resolved = resolveDocumentDeepLinkFromPathname(pathname)
  if (resolved) {
    setPendingDocumentDeepLink(resolved)
  }
  return resolved
}

export type ApplyColdLoadDeepLinkOptions = {
  /**
   * When true (browser reload), keep restored localStorage tabs and only
   * activate / ensure the URL document. When false (default), isolate to a
   * solo document tab - product rule for pasted share URLs in a new Chrome tab.
   */
  preserveSession?: boolean
}

/**
 * Pure helper for cold Chrome-tab deep links.
 *
 * Product rule (maater-design-ui / every-frame): pasted document URL in a new
 * browser tab is an *isolated session* - only that document tab, no Home, no
 * restored localStorage tabs. Workspace focus is applied separately once orgs load.
 *
 * Exception: browser *reload* preserves the open-tab session and activates the
 * address-bar document (continuity on refresh).
 */
export function applyColdLoadDocumentDeepLinkToTabState(
  openTabs: OpenTab[],
  _activeTabId: string | null,
  pending: PendingDocumentDeepLink,
  options: ApplyColdLoadDeepLinkOptions = {},
): { openTabs: OpenTab[]; activeTabId: string } {
  const existing = findOpenTabForDocumentPath(openTabs, pending.pathOrId, [])
  const provisional: OpenTab = existing
    ? {
        ...existing,
        filePath: existing.filePath ?? pending.pathOrId,
        tabSlotId: existing.tabSlotId || 'tab-main',
      }
    : {
        id: pending.pathOrId,
        title: titleHintFromPathOrId(pending.pathOrId),
        type: 'note',
        isDirty: false,
        isPinned: false,
        tabSlotId: 'tab-main',
        filePath: pending.pathOrId,
      }

  if (options.preserveSession) {
    if (existing) {
      const nextTabs = openTabs.map((tab) =>
        tab.id === existing.id ? provisional : tab,
      )
      return { openTabs: nextTabs, activeTabId: provisional.id }
    }
    return {
      openTabs: [...openTabs, provisional],
      activeTabId: provisional.id,
    }
  }

  return { openTabs: [provisional], activeTabId: provisional.id }
}
